import { useEffect, useState } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios'
// eslint-disable-next-line import/no-extraneous-dependencies
import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'
import {
  getAnalyticsEpisodios,
  getAnalyticsEpisodiosExport,
} from '../../services/PredictAnalyticsService/getPredictAnalytics'

// Um lado da conta: o conjunto que a lista mostra e o número que ele vale no painel.
export type DrillLado = {
  conjunto: ConjuntoAnalyticsType
  valor: number
}

// O quadro que foi clicado. Contador tem só `numerador` (abre direto); card de taxa tem os
// dois lados, e o painel alterna entre eles sem fechar — é isso que prova a divisão.
export type DrillAlvo = {
  quadro: string
  numerador: DrillLado
  denominador?: DrillLado
  taxa?: number | null
}

type DrillFiltros = {
  dataInicio: string
  dataFim: string
  vendedorId: number | null
}

const ORDENACAO_PADRAO = {
  campo: 'dataCriacao',
  direcao: 'desc' as 'asc' | 'desc',
}

// Debounce da busca: sem ele cada tecla dispara uma requisição paginada e o servidor
// recebe uma rajada de consultas que já nascem obsoletas.
const ATRASO_BUSCA_MS = 400

export function useAnalyticsDrill({
  dataInicio,
  dataFim,
  vendedorId,
}: DrillFiltros) {
  const [alvo, setAlvo] = useState<DrillAlvo | null>(null)
  const [conjunto, setConjunto] = useState<ConjuntoAnalyticsType | null>(null)
  const [dados, setDados] = useState<GetAnalyticsEpisodiosType | null>(null)
  // Sem estado de erro o painel ficaria preso em "Carregando…" quando a request falhasse:
  // `dados` continua null, `isLoading` volta a false e nenhum bloco do corpo renderiza.
  const [erro, setErro] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isExportando, setIsExportando] = useState(false)
  const [page, setPage] = useState(1)
  const [busca, setBusca] = useState('')
  const [buscaAplicada, setBuscaAplicada] = useState('')
  const [ordenacao, setOrdenacao] = useState(ORDENACAO_PADRAO)

  const abrir = (novoAlvo: DrillAlvo) => {
    setAlvo(novoAlvo)
    setConjunto(novoAlvo.numerador.conjunto)
    setDados(null)
    setErro(null)
    setPage(1)
    setBusca('')
    setBuscaAplicada('')
    setOrdenacao(ORDENACAO_PADRAO)
  }

  const fechar = () => {
    setAlvo(null)
    setConjunto(null)
    setDados(null)
    setErro(null)
    // Uma exportação em voo não pertence ao próximo painel: sem isto o botão do drill
    // seguinte nasce "Gerando…" por causa de um download do anterior.
    setIsExportando(false)
  }

  // Alterna numerador <-> denominador mantendo o painel aberto. A busca é preservada de
  // propósito: quem procurou um cliente quer vê-lo dos dois lados da conta.
  //
  // `setDados(null)` é obrigatório: os metadados (título, definição, total) descrevem o
  // conjunto ANTERIOR até a nova resposta chegar. Mantê-los exibiria "Realizadas — 41" com o
  // chip de "Avaliadas — 149" já marcado como ativo — o desemparelhamento entre rótulo e
  // número que este drill existe justamente para tornar impossível.
  const alternarLado = (novoConjunto: ConjuntoAnalyticsType) => {
    setConjunto(novoConjunto)
    setDados(null)
    setErro(null)
    setPage(1)
  }

  const alternarOrdenacao = (campo: string) => {
    setOrdenacao(atual =>
      atual.campo === campo
        ? { campo, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: 'asc' }
    )
    setPage(1)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaAplicada(busca.trim())
      setPage(1)
    }, ATRASO_BUSCA_MS)
    return () => clearTimeout(timer)
  }, [busca])

  useEffect(() => {
    if (!conjunto) {
      return undefined
    }
    const source = axios.CancelToken.source()
    const carregar = async () => {
      setIsLoading(true)
      const result = await getAnalyticsEpisodios(
        source,
        conjunto,
        dataInicio,
        dataFim,
        page,
        vendedorId,
        buscaAplicada,
        `${ordenacao.campo},${ordenacao.direcao}`
      )
      if ('message' in result) {
        // Cancelada por troca rápida de conjunto/página: outra request já está em voo e vai
        // devolver o loading ao lugar. Sair sem tocar no estado evita piscar a tela.
        if (result.message === 'cancelToken') {
          return
        }
        setErro(result.message)
        toast.error(result.message)
      } else {
        setDados(result)
        setErro(null)
      }
      setIsLoading(false)
    }
    carregar()
    return () => source.cancel('cancelToken')
  }, [
    JSON.stringify({
      conjunto,
      dataInicio,
      dataFim,
      vendedorId,
      page,
      buscaAplicada,
      ordenacao,
    }),
  ])

  // Exporta o conjunto INTEIRO — sem busca e sem paginação. É o caminho que fecha uma
  // auditoria: conferir milhares de episódios pela página 1 de 118 não é conferir.
  const exportar = async () => {
    if (!conjunto) {
      return
    }
    setIsExportando(true)
    const result = await getAnalyticsEpisodiosExport(
      conjunto,
      dataInicio,
      dataFim,
      vendedorId
    )
    if (result instanceof Blob) {
      saveAs(result, `PredictAnalytics_${conjunto}.xlsx`)
    } else {
      // Acima do teto de linhas a API recusa com orientação para reduzir o período —
      // nunca devolve um arquivo cortado, que pareceria completo.
      toast.error(result.message)
    }
    setIsExportando(false)
  }

  return {
    alvo,
    conjunto,
    dados,
    erro,
    isLoading,
    isExportando,
    page,
    setPage,
    busca,
    setBusca,
    ordenacao,
    abrir,
    fechar,
    alternarLado,
    alternarOrdenacao,
    exportar,
  }
}
